<template>
	<div class="container-fluid py-4">
		<div class="row mb-4">
			<div class="col">
				<h1 class="h2">Inspections</h1>
				<p class="text-muted">
					Review inspections recorded across your organization
				</p>
			</div>
		</div>

		<div class="row">
			<div class="col">
				<div class="card">
					<div class="card-body">
						<div v-if="loading" class="text-center py-5 text-muted">
							<p>Loading inspections…</p>
						</div>

						<div v-else class="table-responsive">
							<table class="table table-hover">
								<thead>
									<tr>
										<th>Method</th>
										<th>Start Time</th>
										<th>Duration</th>
										<th>Production</th>
										<th>Stage</th>
										<th>Density</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="inspection in inspections"
										:key="inspection.id"
										style="cursor: pointer"
										@click="router.push(detailRoute(inspection.id))"
									>
										<td>{{ inspection.method || "—" }}</td>
										<td>
											<TimeRelativeWithTooltip
												:time="inspection.start_datetime"
											/>
										</td>
										<td>
											{{
												formatDuration(
													durationSeconds(
														inspection.start_datetime,
														inspection.end_datetime,
													),
												)
											}}
										</td>
										<td>{{ inspection.production ? "Yes" : "No" }}</td>
										<td>{{ inspection.stage || "—" }}</td>
										<td>{{ inspection.density || "—" }}</td>
									</tr>
								</tbody>
							</table>

							<div
								v-if="inspections.length === 0"
								class="text-center py-5 text-muted"
							>
								<p>No inspections found</p>
							</div>
						</div>
					</div>

					<div
						v-if="!loading && inspections.length > 0"
						class="card-footer d-flex justify-content-between align-items-center"
					>
						<button
							class="btn btn-outline-secondary btn-sm"
							:disabled="offset <= 0"
							@click="goPrev"
						>
							<i class="bi bi-chevron-left"></i> Prev
						</button>
						<span class="text-muted small">
							{{ offset + 1 }} – {{ offset + inspections.length }} of
							{{ total }}
						</span>
						<button
							class="btn btn-outline-secondary btn-sm"
							:disabled="offset + inspections.length >= total"
							@click="goNext"
						>
							Next <i class="bi bi-chevron-right"></i>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { durationSeconds, formatDuration } from "@/format";
import { useStoreInspection } from "@/store/inspection";
import type { Inspection } from "@/type/api";

const store = useStoreInspection();
const router = useRouter();

const inspections = ref<Inspection[]>([]);
const loading = ref(false);
const limit = ref(50);
const offset = ref(0);
const total = ref(0);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

const showToast = (title: string, message: string): void => {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
};

const detailRoute = (id: number): { name: string; params: { id: string } } => {
	return { name: "Inspection Review Detail", params: { id: id.toString() } };
};

const goPrev = (): void => {
	offset.value = Math.max(0, offset.value - limit.value);
};

const goNext = (): void => {
	offset.value += limit.value;
};

const load = async (): Promise<void> => {
	loading.value = true;
	try {
		// The page's own total is the envelope's `filtered_total`: the district's
		// live inspections (the collection excludes tombstoned ones), which is also
		// what the pager counts against.
		const page = await store.list(limit.value, offset.value);
		inspections.value = page.items;
		total.value = page.metadata.filtered_total;
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);
		showToast("Error", `Failed to load inspections: ${message}`);
	} finally {
		loading.value = false;
	}
};

// Refetch when pagination moves.
watch(offset, () => {
	void load();
});

onMounted(load);
</script>

<style scoped>
.table th {
	font-weight: 600;
	border-bottom: 2px solid #dee2e6;
}
</style>
